import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { DownloadIcon, LayoutGridIcon, Loader2Icon, XIcon } from 'lucide-react';
import { slides, type DeckSlide } from '../../deckSlides';
import { downloadStoryboardPdf } from '../../utils/exportStoryboard';
import { t } from './primitives';
import { SlidePreview } from './SlidePreview';

/** The storyboard slide itself is excluded so it never previews itself. */
const STORYBOARD_SLIDE_ID = 'scr_stb012';
type StoryboardButtonProps = {
  /** 'light' sits on dark surfaces, 'onAccent' sits on the purple panel. */
  tone?: 'light' | 'onAccent';
  label?: string;
};
export function StoryboardButton({
  tone = 'light',
  label = 'View storyboard'
}: StoryboardButtonProps) {
  const [open, setOpen] = useState(false);
  return <>
      <button type="button" onClick={() => setOpen(true)} className={`${t.label} relative z-20 inline-flex cursor-pointer items-center gap-[0.7vw] rounded-full px-[1.3vw] py-[1vh] font-semibold uppercase tracking-[0.18em] transition-colors ${tone === 'onAccent' ? 'bg-white text-[#5B1FB8] hover:bg-white/90' : 'border border-[#7D38E5] bg-[#7D38E5]/20 text-white hover:bg-[#7D38E5]/35'}`} data-id="element-290" data-mp="3doqofj1o6x3m">
        <LayoutGridIcon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)]" data-id="element-291" data-mp="33xc50kpddux5" />
        {label}
      </button>
      {open ? <Storyboard pages={(slides ?? []).filter(slide => slide.id !== STORYBOARD_SLIDE_ID)} onClose={() => setOpen(false)} data-id="element-292" data-mp="2tymr130lgum6" /> : null}
    </>;
}

/** Renders every slide off-screen and saves them as one downloaded PDF file. */
export function DownloadStoryboardButton({
  label = 'Download PDF',
  pages
}: {
  label?: string;
  pages?: DeckSlide[];
}) {
  const [progress, setProgress] = useState<{
    done: number;
    total: number;
  } | null>(null);
  const exportPages = pages ?? (slides ?? []).filter(slide => slide.id !== STORYBOARD_SLIDE_ID);
  const busy = progress !== null;
  const onClick = async () => {
    if (busy) return;
    setProgress({
      done: 0,
      total: exportPages.length
    });
    try {
      await downloadStoryboardPdf(exportPages, (done, total) => setProgress({
        done,
        total
      }));
    } finally {
      setProgress(null);
    }
  };
  return <button type="button" onClick={onClick} disabled={busy} className={`${t.label} relative z-20 inline-flex cursor-pointer items-center gap-[0.6vw] rounded-full bg-[#7D38E5] px-[1.3vw] py-[1vh] font-semibold uppercase tracking-[0.18em] text-white transition-colors hover:bg-[#6a2ecb] disabled:cursor-wait disabled:opacity-80`} data-id="element-293" data-mp="2fa4hrd0fnh3k">
      {busy ? <Loader2Icon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)] animate-spin" data-id="element-294" data-mp="2ahjtss47jy7y" /> : <DownloadIcon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)]" data-id="element-295" data-mp="3310i4yiyfkmf" />}
      {busy ? `Building PDF · ${progress?.done ?? 0}/${progress?.total ?? 0}` : label}
    </button>;
}
type StoryboardProps = {
  pages: DeckSlide[];
  onClose: () => void;
};
function Storyboard({
  pages,
  onClose
}: StoryboardProps) {
  // Swallow the deck harness' navigation keys while the storyboard is open.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.stopPropagation();
        onClose();
        return;
      }
      if (['ArrowRight', 'ArrowLeft', ' ', 'PageUp', 'PageDown'].includes(event.key)) {
        event.stopPropagation();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);
  if (typeof document === 'undefined') return null;
  return createPortal(<section role="dialog" aria-modal="true" aria-label="Deck storyboard" className="mp-storyboard fixed inset-0 z-[999] overflow-y-auto bg-[#F2F0F6] px-[3vw] py-[3vh] text-[#12101A]" data-id="element-296" data-mp="1p041qpzbq63t">
      <header className="mp-storyboard-chrome mb-[3vh] flex items-end justify-between gap-[2vw]" data-id="element-297" data-mp="3kpihi08zqz4">
        <div data-id="element-298" data-mp="7mb3xrquldkx">
          <p className={`${t.label} font-semibold uppercase tracking-[0.24em] text-[#5B1FB8]`} data-id="element-299" data-mp="7mjg1hf5cev2">
            Storyboard · {pages.length} pages
          </p>
          <h2 className={`${t.title} mt-[1vh] font-semibold leading-[1.05] tracking-[-0.02em]`} data-id="element-300" data-mp="hi4ej5l0ax02">
            Snakker for LEO Pharma
          </h2>
        </div>
        <div className="flex shrink-0 items-center gap-[0.8vw]" data-id="element-301" data-mp="r8pkxf6x95tj">
          <DownloadStoryboardButton pages={pages} data-id="element-302" data-mp="vaavuuzwba13" />
          <button type="button" onClick={onClose} aria-label="Close storyboard" className={`${t.label} inline-flex cursor-pointer items-center gap-[0.6vw] rounded-full border border-black/15 px-[1.3vw] py-[1vh] font-semibold uppercase tracking-[0.18em] text-black/60 transition-colors hover:bg-black/5`} data-id="element-303" data-mp="vbvnabqwycuq">
            <XIcon aria-hidden className="h-[clamp(9px,1.1vw,22px)] w-[clamp(9px,1.1vw,22px)]" data-id="element-304" data-mp="1abrbxinjmzo3" />
            Close
          </button>
        </div>
      </header>

      <ol className="mp-storyboard-grid grid grid-cols-3 gap-x-[2vw] gap-y-[3vh]" data-id="element-305" data-mp="11d5whwddc0rj">
        {pages.map((slide, index) => <li key={slide.id} className="mp-storyboard-page" data-id="element-306" data-mp="1y1lodz3yam7t" data-mpi={index}>
            <div className="overflow-hidden rounded-[0.4vw] border border-black/10 bg-white p-[0.35vw] shadow-sm" data-id="element-307" data-mp="1t30ra8jzwinl">
              <SlidePreview screenId={slide.id} title={slide.title} data-id="element-308" data-mp="1ob96kenit75d" />
            </div>
            <p className={`${t.label} mp-storyboard-caption mt-[1.2vh] flex gap-[0.6vw] leading-[1.3] text-black/60`} data-id="element-309" data-mp="1k9xfs5swufvl">
              <span className="font-semibold text-[#5B1FB8]" data-id="element-310" data-mp="2gupmpxs45n8r">
                {String(index + 1).padStart(2, '0')}
              </span>
              <span data-id="element-311" data-mp="a42u5lqevgwo">{slide.title}</span>
            </p>
          </li>)}
      </ol>

      <p className={`${t.label} mp-storyboard-chrome mt-[3vh] border-t border-black/10 pt-[2vh] text-black/45`} data-id="element-312" data-mp="f3fmgm01xg6n">
        Download PDF renders every slide and saves a landscape,
        one-slide-per-page file to your downloads — it takes a few seconds.
      </p>
    </section>, document.body);
}